// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Section cards: white surface + left orange accent stripe (instead of grey card)
// - Titles in orange, dividers subtle blue
// - Photo gets subtle blue border + rounded corners
// - Added Guardian Information section
// - No API/business logic changed

import React, { useEffect, useState, memo } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Paper,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";

// ✅ Theme constants
const THEME_ORANGE = "#F07828";
const THEME_ORANGE_2 = "#ed7f35ff";
const BG = "#F2F2F2";
const BORDER = "rgba(25,118,210,0.20)";

const EditableField = memo(function EditableField({ label, fieldName, value }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ opacity: 0.75 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          fontWeight: 600,
          color: "rgba(25,118,210,0.90)",
        }}
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );
});

const COLS_XL = 6;

const FieldGrid = ({ children, count }) => {
  const c = count ?? COLS_XL;

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        alignItems: "start",
        gridAutoFlow: "row dense",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: `repeat(${Math.min(c, 4)}, 1fr)`,
          xl: `repeat(${c}, 1fr)`,
        },
      }}
    >
      {children}
    </Box>
  );
};

const sectionCardSx = {
  mb: 1.5,
  borderRadius: 2,
  backgroundColor: "white",
  border: `1px solid ${BORDER}`,
  overflow: "hidden",
  position: "relative",
  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    opacity: 0.95,
  },
};

const sectionTitleSx = {
  color: THEME_ORANGE,
  fontWeight: 900,
  pl: 1,
};

const sectionDividerSx = {
  mb: 1.5,
  borderColor: "rgba(25,118,210,0.12)",
};

const renderSectionCard = (title, fields, count, extraContent = null) => {
  return (
    <Card elevation={0} sx={sectionCardSx}>
      <CardContent sx={{ pl: 2.5, pr: 2, py: 2 }}>
        <Typography variant="h6" gutterBottom sx={sectionTitleSx}>
          {title}
        </Typography>

        <Divider sx={sectionDividerSx} />

        <FieldGrid count={count}>
          {fields.map(([label, fieldName, value]) => (
            <EditableField
              key={`${title}-${fieldName}`}
              label={label}
              fieldName={fieldName}
              value={value}
            />
          ))}

          {extraContent &&
            (React.isValidElement(extraContent)
              ? React.cloneElement(extraContent, { key: `${title}-__extra__` })
              : extraContent)}
        </FieldGrid>
      </CardContent>
    </Card>
  );
};

const renderChildReadOnlySection = (title, rows, fields, count) => {
  return (
    <Card elevation={0} sx={sectionCardSx}>
      <CardContent sx={{ pl: 2.5, pr: 2, py: 2 }}>
        <Typography variant="h6" gutterBottom sx={sectionTitleSx}>
          {title}
        </Typography>

        <Divider sx={sectionDividerSx} />

        {!rows?.length ? (
          <Typography sx={{ color: "text.secondary" }}>No records found.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {rows.map((row, idx) => (
              <Paper
                key={`${title}-${idx}`}
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, borderColor: BORDER }}
              >
                <FieldGrid count={count}>
                  {fields.map((f) => (
                    <EditableField
                      key={`${title}-${idx}-${f.key}`}
                      label={f.label}
                      fieldName={f.key}
                      value={
                        f.type === "boolean"
                          ? row[f.key]
                            ? "Yes"
                            : "No"
                          : row[f.key]
                      }
                    />
                  ))}
                </FieldGrid>
              </Paper>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const ReportStudentPersonalInformation = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState([]);
  const [logincampusCode, setCampusCode] = useState("");
  const [loginUsername, setLoginUsername] = useState("");
  const [passportPreview, setPassportPreview] = useState(null);

  const token = localStorage.getItem("auth");

  const getImageUrl = (value) => {
    if (!value) return null;

    if (
      typeof value === "string" &&
      (value.startsWith("blob:") ||
        value.startsWith("http://") ||
        value.startsWith("https://"))
    ) {
      return value;
    }

    return `${API_UPLOAD_URL}/uploads/${value}`;
  };

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
        setLoginUsername(decodedToken.username || decodedToken.Username || "");
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      if (!logincampusCode || !loginUsername) return;

      try {
        const response = await axios.get(
          `${API_URL}/api/dataReportStudentPersonalInformation?CampusCode=${encodeURIComponent(
            logincampusCode
          )}&Username=${encodeURIComponent(loginUsername)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setData(response.data || []);

        if (response.data?.[0]?.PassportPhoto) {
          setPassportPreview(`${API_UPLOAD_URL}/uploads/${response.data[0].PassportPhoto}`);
        } else {
          setPassportPreview(null);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 401) navigate("/");
      }
    };

    fetchData();
  }, [logincampusCode, loginUsername, navigate, token]);

  const convertToISTDate = (utcTime) =>
    utcTime
      ? new Date(utcTime).toLocaleDateString("en-CA", {
          timeZone: "Asia/Kolkata",
        })
      : "";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = {
    p: 2,
    backgroundColor: BG,
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Information
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              View your personal and academic profile
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          <Stack
            direction={isSmDown ? "column" : "row"}
            spacing={2}
            alignItems={isSmDown ? "flex-start" : "center"}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          />

          {data.length === 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "white",
                border: `1px solid ${BORDER}`,
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "rgba(25,118,210,0.85)", fontWeight: 700 }}>
                No results found
              </Typography>
            </Paper>
          )}

          {data.map((item) => (
            <Box key={item.StudentID}>
              {renderSectionCard(
                "Basic Information",
                [
                  ["Student ID", "StudentID", item.StudentID],
                  ["Full Name", "FullName", item.FullName],
                  ["Aadhar Number", "AadharNumber", item.AadharNumber],
                  ["Admission Date", "AdmissionDate", convertToISTDate(item.AdmissionDate)],
                  ["Academic Year", "AcademicYear", item.AcademicYear],
                  ["Entry", "Entry", item.Entry],
                  ["Qualification", "Qualification", item.Qualification],
                  ["Admission Type", "AdmissionType", item.AdmissionType],
                  ["Hostel Type", "HostelType", item.HostelType],
                  ["Username", "Username", item.Username],
                ],
                6,
                passportPreview && (
                  <Box
                    sx={{
                      gridColumn: { xs: "1 / -1", sm: "1 / -1", lg: "6" },
                      gridRow: { xs: "auto", sm: "auto", lg: "1 / span 2" },
                      display: "flex",
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      component="img"
                      src={passportPreview}
                      alt="Profile"
                      sx={{
                        width: 160,
                        height: 220,
                        borderRadius: 2,
                        objectFit: "cover",
                        border: "1px solid rgba(25,118,210,0.25)",
                        backgroundColor: "rgba(25,118,210,0.04)",
                      }}
                    />
                  </Box>
                )
              )}

              {renderSectionCard(
                "Personal Information",
                [
                  ["Date Of Birth", "DateOfBirth", convertToISTDate(item.DateOfBirth)],
                  ["Gender", "Gender", item.Gender],
                  ["Blood Group", "BloodGroup", item.BloodGroup],
                  ["Religion", "Religion", item.Religion],
                  ["Caste", "Caste", item.Caste],
                  ["SubCaste", "SubCaste", item.SubCaste],
                ],
                6
              )}

              {renderSectionCard(
                "Contact Information",
                [
                  ["Address Line1", "AddressLine1", item.AddressLine1],
                  ["Address Line2", "AddressLine2", item.AddressLine2],
                  ["City", "City", item.City],
                  ["District", "District", item.District],
                  ["State", "State", item.State],
                  ["Zip", "Zip", item.Zip],
                  ["Phone Number", "PhoneNumber", item.PhoneNumber],
                  ["Alternate Phone Number", "AlternatePhoneNumber", item.AlternatePhoneNumber],
                  ["Email Address", "EmailAddress", item.EmailAddress],
                ],
                3
              )}

              {renderSectionCard(
                "Family Information",
                [
                  ["Father Name", "FatherFullName", item.FatherFullName],
                  ["Father Aadhar", "FatherAadharNumber", item.FatherAadharNumber],
                  ["Father Qualification", "FatherQualification", item.FatherQualification],
                  ["Father Occupation", "FatherOccupation", item.FatherOccupation],
                  ["Father Phone Number", "FatherPhoneNumber", item.FatherPhoneNumber],
                  ["Mother Name", "MotherFullName", item.MotherFullName],
                  ["Mother Aadhar", "MotherAadharNumber", item.MotherAadharNumber],
                  ["Mother Qualification", "MotherQualification", item.MotherQualification],
                  ["Mother Occupation", "MotherOccupation", item.MotherOccupation],
                  ["Mother Phone Number", "MotherPhoneNumber", item.MotherPhoneNumber],
                ],
                5
              )}

              {renderSectionCard(
                "Academic Information",
                [
                  ["Campus Code", "CampusCode", item.CampusCode],
                  ["SubCampus Code", "SubCampusCode", item.SubCampusCode],
                  ["Degree Code", "DegreeCode", item.DegreeCode],
                  ["Branch Code", "BranchCode", item.BranchCode],
                  ["IsStudentActive", "IsStudentActive", item.IsStudentActive],
                ],
                5
              )}

              {renderChildReadOnlySection(
                "Education Details",
                item.EducationDetails || [],
                [
                  { label: "Level", key: "EducationLevel" },
                  { label: "Board Type", key: "BoardType" },
                  { label: "Institution", key: "InstitutionName" },
                  { label: "Hall Ticket", key: "HallTicketNumber" },
                  { label: "Percentage", key: "Percentage" },
                  { label: "CGPA", key: "CGPA" },
                  { label: "City", key: "InstitutionCity" },
                  { label: "State", key: "InstitutionState" },
                  { label: "Passed Out Year", key: "PassedOutYear" },
                ],
                9
              )}

              {renderChildReadOnlySection(
                "Competitive / Entrance Exams",
                item.CompetitiveExams || [],
                [
                  { label: "Exam Type", key: "ExamType" },
                  { label: "Hall Ticket", key: "HallTicketNumber" },
                  { label: "Rank", key: "RankNo" },
                  { label: "Score", key: "Score" },
                  { label: "Year", key: "ExamYear" },
                ],
                5
              )}

              {renderChildReadOnlySection(
                "Document Information",
                item.DocumentDetails || [],
                [
                  { label: "Document Type", key: "DocumentType" },
                  { label: "Received", key: "IsReceived", type: "boolean" },
                  { label: "Verified", key: "IsVerified", type: "boolean" },
                  { label: "Comments", key: "Comments" },
                ],
                4
              )}

              {renderSectionCard(
                "Reference Information",
                [
                  ["Reference Name", "ReferenceName", item.ReferenceName],
                  ["Reference Phone Number", "ReferencePhoneNumber", item.ReferencePhoneNumber],
                ],
                2
              )}

              {renderSectionCard(
                "Guardian Information",
                [
                  ["Guardian 1 Full Name", "Guardian1FullName", item.Guardian1FullName],
                  ["Guardian 2 Full Name", "Guardian2FullName", item.Guardian2FullName],
                  ["Guardian 3 Full Name", "Guardian3FullName", item.Guardian3FullName],
                ],
                3,
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(3, 1fr)",
                      },
                    }}
                  >
                    {[1, 2, 3].map((n) => {
                      const imgField = `Guardian${n}Image`;
                      const imgUrl = getImageUrl(item[imgField]);

                      return (
                        <Box key={imgField}>
                          <Typography variant="subtitle2" sx={{ opacity: 0.75 }}>
                            Guardian {n} Image
                          </Typography>

                          {imgUrl ? (
                            <Box
                              component="img"
                              src={imgUrl}
                              alt={`Guardian ${n}`}
                              sx={{
                                width: 140,
                                height: 160,
                                borderRadius: 2,
                                objectFit: "cover",
                                border: "1px solid rgba(25,118,210,0.25)",
                                backgroundColor: "rgba(25,118,210,0.04)",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 140,
                                height: 160,
                                borderRadius: 2,
                                border: "1px dashed rgba(25,118,210,0.30)",
                                backgroundColor: "rgba(25,118,210,0.04)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "text.secondary",
                              }}
                            >
                              No image
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportStudentPersonalInformation;